import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, Download, Share2, DollarSign, MapPin, Clock, 
  CheckCircle, XCircle, RefreshCw, Users, TrendingUp,
  Eye, Globe, Smartphone, ChevronDown, ChevronUp, Search,
  AlertCircle, Crown, Copy, Check, Bell, BellRing, Volume2, VolumeX
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const ADMIN_KEY = 'AETHER2025';

// Notification sound (base64 encoded short beep)
const NOTIFICATION_SOUND = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1ubX9viIWDbX6JkpWQh4V9cnB4fnt7enl7goWCfnl8foOEfnd0cW9ydHR0c3J0eH1/gH57d3h9goWCfXh3eH2AgHx4dXd7f4GBf3p3d3p+gYKAfHh3eX2AgYF+eXZ4e3+CgoF9eHZ4fICCgn97eHZ5fYCCgn98eHZ5fYCCgX98eHd5fYCBgX58eHd6fYCBgH58eXh6fH+AgH58eXh6fH6AgH58eXl6fH6Af358eXl6e36Af358eXl7fH6Af358eXl7fH6Af358eXl7fH5/fn58eXl7fH5/fn58eXp7fH5/fn58eXp7fH5/fn58enp7fH5/fn58enp7e35/fn58enp7e31/fn58enp7e31+fn58enp7e31+fn58enp7e31+fn58enp7e31+fn58enp7e31+fn58enp7e31+fn58enp7e31+fn58enp7e31+fn58enp7e31+fn58enp7e31+fn58';

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminKey, setAdminKey] = useState('');
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    pending: true,
    purchases: true,
    downloads: true,
    shares: true,
    events: false,
    notifications: true
  });
  const [copiedToken, setCopiedToken] = useState('');
  
  // Notification state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const lastUnreadCount = useRef(0);
  const audioRef = useRef(null);

  // Request browser notification permission
  useEffect(() => {
    if (isAuthenticated && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [isAuthenticated]);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, [soundEnabled]);

  // Show browser notification
  const showBrowserNotification = useCallback((title, body) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, {
        body,
        icon: '/icons/icon-192x192.png',
        badge: '/icons/icon-72x72.png',
        tag: 'skybot-admin'
      });
    }
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const response = await axios.get(`${API}/admin/notifications?admin_key=${ADMIN_KEY}`);
      setNotifications(response.data.notifications || []);
      const newUnreadCount = response.data.unread_count || 0;
      
      // Check if we have new notifications
      if (newUnreadCount > lastUnreadCount.current && lastUnreadCount.current > 0) {
        playNotificationSound();
        const latestNotification = response.data.notifications?.[0];
        if (latestNotification && !latestNotification.read) {
          showBrowserNotification('💰 New Payment!', latestNotification.message);
          toast.success(latestNotification.message, { duration: 10000 });
        }
      }
      
      lastUnreadCount.current = newUnreadCount;
      setUnreadCount(newUnreadCount);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  }, [isAuthenticated, playNotificationSound, showBrowserNotification]);

  // Mark notifications as read
  const markAllRead = async () => {
    try {
      await axios.post(`${API}/admin/notifications/mark-read?admin_key=${ADMIN_KEY}`);
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success('All notifications marked as read');
    } catch (err) {
      toast.error('Failed to mark notifications as read');
    }
  };

  const fetchDashboard = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      const response = await axios.get(`${API}/admin/dashboard?admin_key=${ADMIN_KEY}`);
      setDashboardData(response.data);
    } catch (err) {
      setError('Failed to fetch dashboard data');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchDashboard();
      fetchNotifications();
      // Auto-refresh every 10 seconds for real-time updates
      const dashboardInterval = setInterval(fetchDashboard, 30000);
      const notificationInterval = setInterval(fetchNotifications, 10000);
      return () => {
        clearInterval(dashboardInterval);
        clearInterval(notificationInterval);
      };
    }
  }, [isAuthenticated, fetchDashboard, fetchNotifications]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (adminKey === ADMIN_KEY) {
      setIsAuthenticated(true);
      toast.success('Welcome, Admin!');
    } else {
      toast.error('Invalid admin key');
    }
  };

  const verifyPayment = async (session) => {
    try {
      const response = await axios.post(`${API}/payment/admin/verify-payment`, {
        activation_token: session.activation_token,
        admin_key: ADMIN_KEY
      });
      
      if (response.data.success) {
        toast.success(`Payment verified for ${session.email}!`);
        fetchDashboard();
        fetchNotifications();
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      toast.error('Failed to verify payment');
      console.error(err);
    }
  };

  const copyToken = (token) => {
    navigator.clipboard.writeText(token);
    setCopiedToken(token);
    toast.success('Token copied!');
    setTimeout(() => setCopiedToken(''), 2000);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-400 bg-yellow-400/10';
      case 'payment_verified': return 'text-blue-400 bg-blue-400/10';
      case 'completed': return 'text-green-400 bg-green-400/10';
      case 'expired': return 'text-red-400 bg-red-400/10';
      default: return 'text-gray-400 bg-gray-400/10';
    }
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#02040a] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md p-8 rounded-3xl"
          style={{
            background: 'linear-gradient(145deg, #0f1420 0%, #0a0d14 100%)',
            border: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Admin Access</h1>
              <p className="text-slate-400 text-sm">Skybot Control Center</p>
            </div>
          </div>
          
          <form onSubmit={handleLogin}>
            <label className="text-slate-400 text-xs mb-2 block">Admin Key</label>
            <input
              type="password"
              value={adminKey}
              onChange={(e) => setAdminKey(e.target.value)}
              placeholder="Enter admin key..."
              className="w-full px-4 py-3 bg-[#0a0f1a] border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 mb-4"
              autoFocus
            />
            <button
              type="submit"
              className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 transition-all"
            >
              Access Dashboard
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-[#02040a] text-white p-4 md:p-6">
      {/* Hidden audio element for notification sound */}
      <audio ref={audioRef} src={NOTIFICATION_SOUND} preload="auto" />
      
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Admin Dashboard</h1>
            <p className="text-slate-400 text-xs">Skybot Analytics & Payments</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Sound Toggle */}
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={`p-2 rounded-xl transition-all ${soundEnabled ? 'bg-green-500/20 text-green-400' : 'bg-white/5 text-slate-400'}`}
            title={soundEnabled ? 'Sound on' : 'Sound off'}
          >
            {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
          
          {/* Notification Bell */}
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative p-2 rounded-xl bg-white/5 hover:bg-white/10 transition-all"
          >
            {unreadCount > 0 ? (
              <BellRing className="w-5 h-5 text-yellow-400 animate-pulse" />
            ) : (
              <Bell className="w-5 h-5 text-slate-400" />
            )}
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs font-bold flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          
          {/* Refresh Button */}
          <button
            onClick={() => { fetchDashboard(); fetchNotifications(); }}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition-all"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Notification Dropdown */}
      <AnimatePresence>
        {showNotifications && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute right-4 top-20 w-96 max-h-96 overflow-y-auto rounded-2xl z-50"
            style={{
              background: 'linear-gradient(145deg, #0f1420 0%, #0a0d14 100%)',
              border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 25px 50px rgba(0,0,0,0.5)'
            }}
          >
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="font-bold text-white flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Notifications
              </h3>
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-xs text-cyan-400 hover:text-cyan-300"
                >
                  Mark all read
                </button>
              )}
            </div>
            
            <div className="max-h-72 overflow-y-auto">
              {notifications.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-8">No notifications yet</p>
              ) : (
                notifications.slice(0, 20).map((notification) => (
                  <div
                    key={notification.notification_id}
                    className={`p-3 border-b border-white/5 ${!notification.read ? 'bg-yellow-500/5' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        notification.type === 'new_payment' ? 'bg-green-500/20' : 'bg-blue-500/20'
                      }`}>
                        <DollarSign className={`w-4 h-4 ${
                          notification.type === 'new_payment' ? 'text-green-400' : 'text-blue-400'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium">{notification.title}</p>
                        <p className="text-slate-400 text-xs">{notification.message}</p>
                        <p className="text-slate-600 text-xs mt-1">{formatTimeAgo(notification.created_at)}</p>
                      </div>
                      {!notification.read && (
                        <div className="w-2 h-2 rounded-full bg-yellow-400" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {error && (
        <div className="mb-4 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {/* Live Notification Banner */}
      {unreadCount > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 p-4 rounded-xl bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-500/30 flex items-center justify-center">
              <BellRing className="w-5 h-5 text-green-400 animate-pulse" />
            </div>
            <div>
              <p className="text-green-400 font-bold">
                {unreadCount} New Payment{unreadCount > 1 ? 's' : ''} Waiting!
              </p>
              <p className="text-green-400/70 text-sm">Check the pending payments section below</p>
            </div>
          </div>
          <button
            onClick={() => setShowNotifications(true)}
            className="px-4 py-2 rounded-xl bg-green-500 hover:bg-green-400 text-white font-medium transition-all"
          >
            View
          </button>
        </motion.div>
      )}

      {/* Summary Cards */}
      {dashboardData && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
          <SummaryCard
            icon={Download}
            label="Downloads"
            value={dashboardData.summary.total_downloads}
            color="from-green-500 to-emerald-500"
          />
          <SummaryCard
            icon={Share2}
            label="Shares"
            value={dashboardData.summary.total_shares}
            color="from-blue-500 to-cyan-500"
          />
          <SummaryCard
            icon={Eye}
            label="Page Views"
            value={dashboardData.summary.total_page_views}
            color="from-purple-500 to-pink-500"
          />
          <SummaryCard
            icon={Crown}
            label="Purchases"
            value={dashboardData.summary.total_purchases}
            color="from-yellow-500 to-orange-500"
          />
          <SummaryCard
            icon={Clock}
            label="Pending"
            value={dashboardData.summary.pending_payments}
            color="from-yellow-400 to-amber-500"
            highlight={dashboardData.summary.pending_payments > 0}
          />
          <SummaryCard
            icon={CheckCircle}
            label="Awaiting Activation"
            value={dashboardData.summary.awaiting_activation}
            color="from-blue-400 to-indigo-500"
          />
        </div>
      )}

      {/* Main Content */}
      {dashboardData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Pending Payments - Action Required */}
          <Section
            title="Pending Payments"
            subtitle="Verify after Cash App payment received"
            icon={Clock}
            color="yellow"
            isExpanded={expandedSections.pending}
            onToggle={() => toggleSection('pending')}
            badge={dashboardData.pending_sessions.filter(s => s.status === 'pending').length}
          >
            {dashboardData.pending_sessions.filter(s => s.status === 'pending').length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-4">No pending payments</p>
            ) : (
              <div className="space-y-2">
                {dashboardData.pending_sessions
                  .filter(s => s.status === 'pending')
                  .map((session) => (
                    <div
                      key={session.session_id}
                      className="p-3 rounded-xl bg-white/5 border border-white/10"
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div>
                          <p className="text-white font-medium text-sm">{session.email}</p>
                          <p className="text-slate-400 text-xs">
                            {session.plan} - ${session.amount}
                          </p>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(session.status)}`}>
                          {session.status}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-slate-500 text-xs">Token:</span>
                        <code className="font-mono text-cyan-400 text-sm">{session.activation_token}</code>
                        <button
                          onClick={() => copyToken(session.activation_token)}
                          className="p-1 rounded hover:bg-white/10"
                        >
                          {copiedToken === session.activation_token ? (
                            <Check className="w-3 h-3 text-green-400" />
                          ) : (
                            <Copy className="w-3 h-3 text-slate-400" />
                          )}
                        </button>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500 text-xs">
                          {formatDate(session.created_at)}
                        </span>
                        <button
                          onClick={() => verifyPayment(session)}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-green-500 hover:bg-green-400 text-white transition-colors"
                        >
                          Verify Payment
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </Section>

          {/* Awaiting Activation */}
          <Section
            title="Awaiting User Activation"
            subtitle="Payment verified, waiting for user"
            icon={CheckCircle}
            color="blue"
            isExpanded={expandedSections.pending}
            onToggle={() => toggleSection('pending')}
            badge={dashboardData.pending_sessions.filter(s => s.status === 'payment_verified').length}
          >
            {dashboardData.pending_sessions.filter(s => s.status === 'payment_verified').length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-4">No payments awaiting activation</p>
            ) : (
              <div className="space-y-2">
                {dashboardData.pending_sessions
                  .filter(s => s.status === 'payment_verified')
                  .map((session) => (
                    <div
                      key={session.session_id}
                      className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/30"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-white font-medium text-sm">{session.email}</p>
                          <p className="text-slate-400 text-xs">
                            {session.plan} - ${session.amount} • Token: {session.activation_token}
                          </p>
                        </div>
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium text-blue-400 bg-blue-400/10">
                          Verified ✓
                        </span>
                      </div>
                      <p className="text-slate-500 text-xs mt-1">
                        Verified: {formatDate(session.verified_at)}
                      </p>
                    </div>
                  ))}
              </div>
            )}
          </Section>

          {/* Completed Purchases */}
          <Section
            title="Recent Purchases"
            subtitle="Completed premium activations"
            icon={Crown}
            color="green"
            isExpanded={expandedSections.purchases}
            onToggle={() => toggleSection('purchases')}
            badge={dashboardData.completed_purchases.length}
          >
            {dashboardData.completed_purchases.length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-4">No purchases yet</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {dashboardData.completed_purchases.slice(0, 10).map((purchase) => (
                  <div
                    key={purchase.session_id}
                    className="p-3 rounded-xl bg-green-500/10 border border-green-500/30"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-white font-medium text-sm">{purchase.email}</p>
                        <p className="text-green-400 text-xs font-medium">
                          {purchase.plan} - ${purchase.amount}
                        </p>
                      </div>
                      <Crown className="w-4 h-4 text-yellow-400" />
                    </div>
                    <p className="text-slate-500 text-xs mt-1">
                      Activated: {formatDate(purchase.activated_at)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Downloads by Location */}
          <Section
            title="Downloads by Location"
            subtitle="Geographic distribution"
            icon={MapPin}
            color="cyan"
            isExpanded={expandedSections.downloads}
            onToggle={() => toggleSection('downloads')}
          >
            {dashboardData.downloads_by_location.length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-4">No location data yet</p>
            ) : (
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {dashboardData.downloads_by_location.map((loc, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between p-2 rounded-lg bg-white/5"
                  >
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-cyan-400" />
                      <span className="text-white text-sm">
                        {loc.city ? `${loc.city}, ` : ''}{loc.country || 'Unknown'}
                      </span>
                    </div>
                    <span className="text-cyan-400 font-medium">{loc.count}</span>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Shares by Location */}
          <Section
            title="Shares by Location"
            subtitle="Where app is being shared"
            icon={Share2}
            color="purple"
            isExpanded={expandedSections.shares}
            onToggle={() => toggleSection('shares')}
          >
            {dashboardData.shares_by_location.length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-4">No share data yet</p>
            ) : (
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {dashboardData.shares_by_location.map((loc, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between p-2 rounded-lg bg-white/5"
                  >
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-purple-400" />
                      <span className="text-white text-sm">
                        {loc.city ? `${loc.city}, ` : ''}{loc.country || 'Unknown'}
                      </span>
                    </div>
                    <span className="text-purple-400 font-medium">{loc.count}</span>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Recent Events */}
          <Section
            title="Recent Events"
            subtitle="Live activity feed"
            icon={TrendingUp}
            color="pink"
            isExpanded={expandedSections.events}
            onToggle={() => toggleSection('events')}
            fullWidth
          >
            {dashboardData.recent_events.length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-4">No events yet</p>
            ) : (
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {dashboardData.recent_events.slice(0, 20).map((event) => (
                  <div
                    key={event.event_id}
                    className="flex items-center gap-3 p-2 rounded-lg bg-white/5"
                  >
                    <EventIcon type={event.event_type} />
                    <div className="flex-1 min-w-0">
                      <span className="text-white text-sm capitalize">{event.event_type.replace('_', ' ')}</span>
                      {event.location?.country && (
                        <span className="text-slate-500 text-xs ml-2">
                          {event.location.city ? `${event.location.city}, ` : ''}{event.location.country}
                        </span>
                      )}
                    </div>
                    <span className="text-slate-500 text-xs whitespace-nowrap">
                      {formatDate(event.created_at)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      )}

      {/* Loading State */}
      {isLoading && !dashboardData && (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-8 h-8 text-cyan-400 animate-spin" />
        </div>
      )}
    </div>
  );
};

// Summary Card Component
const SummaryCard = ({ icon: Icon, label, value, color, highlight }) => (
  <motion.div
    whileHover={{ scale: 1.02 }}
    className={`p-4 rounded-2xl ${highlight ? 'ring-2 ring-yellow-400 ring-offset-2 ring-offset-[#02040a]' : ''}`}
    style={{
      background: 'linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
      border: '1px solid rgba(255,255,255,0.1)'
    }}
  >
    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center mb-2`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    <p className="text-slate-400 text-xs">{label}</p>
  </motion.div>
);

// Section Component
const Section = ({ title, subtitle, icon: Icon, color, children, isExpanded, onToggle, badge, fullWidth }) => {
  const colorClasses = {
    yellow: 'from-yellow-500 to-amber-500',
    blue: 'from-blue-500 to-cyan-500',
    green: 'from-green-500 to-emerald-500',
    cyan: 'from-cyan-500 to-teal-500',
    purple: 'from-purple-500 to-pink-500',
    pink: 'from-pink-500 to-rose-500'
  };

  return (
    <div
      className={`rounded-2xl overflow-hidden ${fullWidth ? 'lg:col-span-2' : ''}`}
      style={{
        background: 'linear-gradient(145deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
        border: '1px solid rgba(255,255,255,0.1)'
      }}
    >
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center`}>
            <Icon className="w-4 h-4 text-white" />
          </div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <h3 className="text-white font-medium text-sm">{title}</h3>
              {badge !== undefined && badge > 0 && (
                <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-white/10 text-white">
                  {badge}
                </span>
              )}
            </div>
            <p className="text-slate-500 text-xs">{subtitle}</p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="px-4 pb-4"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Event Icon Component
const EventIcon = ({ type }) => {
  const icons = {
    download: <Download className="w-4 h-4 text-green-400" />,
    share: <Share2 className="w-4 h-4 text-blue-400" />,
    page_view: <Eye className="w-4 h-4 text-purple-400" />,
    premium_click: <Crown className="w-4 h-4 text-yellow-400" />
  };
  
  return icons[type] || <Globe className="w-4 h-4 text-slate-400" />;
};

export default AdminDashboard;
